import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import { useFormik } from 'formik'
import { useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import { UPDATE_RESOURCE_CATEGORY } from '../../../gql/mutations/resourceCategoryMutations'
import { IUpdateResourceCategoryResponse, IResourceCategory } from '../../../types/resourceCategory'
import { parseId } from '../../../helpers'
import { flattenCategoryTree } from '../categoryTreeHelpers'
import { IntlShape, useIntl } from 'react-intl'
import { BACKEND_ERROR, REQUIRED_MARK, RESOURCE_MESSAGES as R_ } from '../resourceCenterMessages'

/** Fabrica: el mensaje necesita `intl`, asi que el esquema no puede ser un const. */
const buildValidationSchema = (intl: IntlShape) =>
  Yup.object().shape({
    name: Yup.string().required(intl.formatMessage(R_.vNameRequired)),
  })

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  categories: IResourceCategory[]
  category: IResourceCategory
}

export const ModalEditCategory: React.FC<IModalProps> = ({
  isOpen,
  onClose,
  refetch,
  categories,
  category,
}) => {
  const intl = useIntl()
  const [updateCategory, { data, loading, error }] =
    useMutation<IUpdateResourceCategoryResponse>(UPDATE_RESOURCE_CATEGORY)

  const formik = useFormik({
    initialValues: {
      name: category.name,
      parentId: category.parent?.id || '',
      order: category.order,
    },
    enableReinitialize: true,
    validationSchema: buildValidationSchema(intl),
    onSubmit: async (v) => {
      try {
        await updateCategory({
          variables: {
            id: parseId(category.id),
            categoryData: {
              name: v.name,
              parentId: v.parentId ? parseId(v.parentId) : null,
              order: v.order,
            },
          },
        })
      } catch (err) {
        console.error('Error updating category:', err)
        toast.error(intl.formatMessage(R_.errUpdateFolder))
      }
    },
  })

  useEffect(() => {
    if (error) toast.error(intl.formatMessage(BACKEND_ERROR, {message: error.message}))
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.updateResourceCategory.success) {
      onClose()
      refetch()
      toast.success(intl.formatMessage(R_.toastFolderUpdated))
    } else if (
      data.updateResourceCategory.errors &&
      data.updateResourceCategory.errors.length > 0
    ) {
      toast.error(intl.formatMessage(BACKEND_ERROR, {message: data.updateResourceCategory.errors[0].message}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleClose = () => {
    formik.resetForm()
    onClose()
  }

  // Excluye la propia categoría y su subárbol para evitar ciclos al mover
  const parentOptions = flattenCategoryTree(categories, category.id)
  const selectedParent = parentOptions.find((opt) => opt.value === formik.values.parentId) || null

  return (
    <Modal show={isOpen} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-pencil" /> {intl.formatMessage(R_.editFolder)}
          {loading && <Spinner animation="border" size="sm" />}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">
            {intl.formatMessage(R_.name)}
            <span className="text-danger ms-1">{REQUIRED_MARK}</span>
            </Form.Label>
            <Form.Control
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={Boolean(formik.errors.name && formik.touched.name)}
              autoFocus
            />
            {formik.errors.name && formik.touched.name && (
              <Form.Text className="text-danger">{formik.errors.name}</Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">{intl.formatMessage(R_.parentFolder)}</Form.Label>
            <Select
              isClearable
              value={selectedParent}
              onChange={(option) => formik.setFieldValue('parentId', option?.value || '')}
              options={parentOptions}
              placeholder={intl.formatMessage(R_.noneRootFolder)}
            />
            <Form.Text className="text-muted">
              {intl.formatMessage(R_.parentMoveHint)}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">{intl.formatMessage(R_.displayOrder)}</Form.Label>
            <Form.Control
              type="number"
              name="order"
              value={formik.values.order}
              onChange={formik.handleChange}
              min="0"
            />
            <Form.Text className="text-muted">{intl.formatMessage(R_.orderHint)}</Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
        {intl.formatMessage(R_.cancel)}
        </Button>
        <Button variant="primary" onClick={() => formik.handleSubmit()} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {intl.formatMessage(R_.saving)}
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-1" /> {intl.formatMessage(R_.saveChanges)}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
